import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { ResourceSeries } from './useResourceSeries';

export interface ResourceSermon {
  id: string;
  series_id: string;
  title: string;
  scripture: string | null;
  sermon_notes: string | null;
  sermon_order: number;
  created_at: string;
}

export interface ResourceAsset {
  id: string;
  series_id: string;
  sermon_id: string | null;
  label: string;
  asset_type: 'graphic' | 'social' | 'slide' | 'outline';
  file_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
}

export function useSeriesDetail(seriesId: string | undefined) {
  const [series, setSeries] = useState<ResourceSeries | null>(null);
  const [sermons, setSermons] = useState<ResourceSermon[]>([]);
  const [assets, setAssets] = useState<ResourceAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!seriesId) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [seriesResult, sermonsResult, assetsResult] = await Promise.all([
          supabase.from('resource_series').select('*').eq('id', seriesId).single(),
          supabase.from('resource_sermons').select('*').eq('series_id', seriesId).order('sermon_order'),
          supabase.from('resource_assets').select('*').eq('series_id', seriesId),
        ]);

        if (!mounted) return;

        if (seriesResult.error) throw seriesResult.error;
        if (sermonsResult.error) throw sermonsResult.error;
        if (assetsResult.error) throw assetsResult.error;

        setSeries(seriesResult.data as ResourceSeries);
        setSermons(sermonsResult.data as ResourceSermon[]);
        setAssets(assetsResult.data as ResourceAsset[]);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load series');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [seriesId]);

  return { series, sermons, assets, loading, error };
}
